import { Client, SpeechNeed } from './interfaces';

type SpeechNeedCarrier = {
    speechNeed?: unknown;
    speechNeeds?: unknown;
    userType?: Client['userType'] | string;
};

function normaliseSpeechNeedId(value: unknown): string | null {
    if (typeof value !== 'string') {
        return null;
    }

    const speechNeedId = value.trim();
    return speechNeedId || null;
}

function normaliseSpeechNeedIds(value: unknown): string[] {
    if (Array.isArray(value)) {
        return Array.from(new Set(
            value
                .map(normaliseSpeechNeedId)
                .filter((speechNeedId): speechNeedId is string => speechNeedId !== null)
        ));
    }

    if (typeof value === 'string') {
        return Array.from(new Set(
            value
                .split(/[;,]/)
                .map(normaliseSpeechNeedId)
                .filter((speechNeedId): speechNeedId is string => speechNeedId !== null)
        ));
    }

    return [];
}

export function getClientSpeechNeeds(client: SpeechNeedCarrier): string[] {
    if (client.speechNeeds !== undefined) {
        return normaliseSpeechNeedIds(client.speechNeeds);
    }

    return normaliseSpeechNeedIds(client.speechNeed);
}

export function getPrimaryClientSpeechNeed(client: SpeechNeedCarrier): string {
    return getClientSpeechNeeds(client)[0] || '';
}

export function getClientSpeechNeedNames(client: SpeechNeedCarrier, speechNeeds: SpeechNeed[]): string {
    return getClientSpeechNeeds(client)
        .map((speechNeedId) => speechNeeds.find((speechNeed) => speechNeed.id === speechNeedId)?.name || speechNeedId)
        .join(', ');
}

export function clientHasSpeechNeedFields(client: SpeechNeedCarrier): boolean {
    return client.speechNeeds !== undefined || client.speechNeed !== undefined;
}

export function isCareRecipientClient(client: SpeechNeedCarrier): boolean {
    return client.userType === 'care_recipient' || client.userType === 'client';
}

export function shouldPersistClientSpeechNeeds(client: SpeechNeedCarrier): boolean {
    return clientHasSpeechNeedFields(client) || isCareRecipientClient(client);
}

export function withClientSpeechNeeds<T extends Client>(client: T): T {
    if (!shouldPersistClientSpeechNeeds(client)) {
        return client;
    }

    const speechNeeds = getClientSpeechNeeds(client);
    return {
        ...client,
        speechNeeds,
        speechNeed: speechNeeds[0] || '',
    };
}
